import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  Building2, 
  Users, 
  Stethoscope, 
  Calendar, 
  Package, 
  Settings,
  LogOut,
  LayoutDashboard,
  Crown,
  UserCog,
  Code2
} from 'lucide-react';

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  // Super admin menu items
  const superAdminMenu = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/hospitals', icon: Building2, label: 'Hospitals' },
    { path: '/admin/subscriptions', icon: Crown, label: 'Subscriptions' },
    { path: '/admin/all-doctors', icon: Stethoscope, label: 'All Doctors' },
    { path: '/admin/all-patients', icon: Users, label: 'All Patients' },
    { path: '/admin/appointments', icon: Calendar, label: 'Appointments' },
  ];

  // Hospital admin menu items
  const hospitalAdminMenu = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/departments', icon: Package, label: 'Departments' },
    { path: '/admin/services', icon: Settings, label: 'Services' },
    { path: '/admin/doctors', icon: Stethoscope, label: 'Doctors' },
    { path: '/admin/appointments', icon: Calendar, label: 'Appointments' },
    { path: '/admin/patients', icon: Users, label: 'Patients' },
    { path: '/admin/integrations', icon: Code2, label: 'Integrations' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const menuItems = user?.role === 'super_admin' ? superAdminMenu : hospitalAdminMenu;

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col" data-testid="admin-sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            {user?.role === 'super_admin' ? (
              <Crown className="h-6 w-6 text-amber-500" />
            ) : (
              <UserCog className="h-6 w-6 text-primary" />
            )}
            <div>
              <h2 className="font-manrope font-bold text-lg">
                {user?.role === 'super_admin' ? 'Super Admin' : 'Admin Panel'}
              </h2>
              <p className="text-xs text-slate-500">{user?.full_name}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  active
                    ? 'bg-primary text-white'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm">
              <p className="font-medium text-slate-900">{user?.full_name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleLogout}
            data-testid="admin-logout"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  );
};